"use client";

import { useTranslations } from "next-intl";
import { SectionShell, EditorialTitle, GradientText, RevealOnScroll } from "./_primitives";

export default function PillarsSection() {
    const t = useTranslations();

    const pillars = [
        {
            num: "01",
            tag: t("joinInstructor_pillar1_tag"),
            title: t("joinInstructor_pillar1_title"),
            desc: t("joinInstructor_pillar1_desc"),
            rows: [
                { label: t("joinInstructor_pillar1_row1_label"), value: t("joinInstructor_pillar1_row1_value") },
                { label: t("joinInstructor_pillar1_row2_label"), value: t("joinInstructor_pillar1_row2_value") },
                { label: t("joinInstructor_pillar1_row3_label"), value: t("joinInstructor_pillar1_row3_value") },
            ],
        },
        {
            num: "02",
            tag: t("joinInstructor_pillar2_tag"),
            title: t("joinInstructor_pillar2_title"),
            desc: t("joinInstructor_pillar2_desc"),
            highlight: t("joinInstructor_pillar2_highlight"),
            footer: t("joinInstructor_pillar2_footer"),
        },
        {
            num: "03",
            tag: t("joinInstructor_pillar3_tag"),
            title: t("joinInstructor_pillar3_title"),
            desc: t("joinInstructor_pillar3_desc"),
            rows: [
                { label: t("joinInstructor_pillar3_row1_label"), value: t("joinInstructor_pillar3_row1_value") },
                { label: t("joinInstructor_pillar3_row2_label"), value: t("joinInstructor_pillar3_row2_value") },
                { label: t("joinInstructor_pillar3_row3_label"), value: t("joinInstructor_pillar3_row3_value") },
            ],
        },
    ];

    return (
        <SectionShell label={t("joinInstructor_pillars_marker")}>
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-end mb-12 sm:mb-16">
                <RevealOnScroll>
                    <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-5">
                        {t("joinInstructor_pillars_eyebrow")}
                    </p>
                    <EditorialTitle className="text-4xl sm:text-5xl md:text-6xl">
                        {t("joinInstructor_pillars_title_part1")}
                        <br />
                        <GradientText>{t("joinInstructor_pillars_title_part2")}</GradientText>
                    </EditorialTitle>
                </RevealOnScroll>
                <RevealOnScroll delay={0.1}>
                    <p
                        className="text-gray-500 dark:text-white/55 italic text-lg lg:text-right max-w-md lg:ml-auto"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinInstructor_pillars_subtitle")}
                    </p>
                </RevealOnScroll>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
                {pillars.map((p, i) => (
                    <RevealOnScroll key={p.num} delay={i * 0.08}>
                        <div className="relative h-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-7 flex flex-col">
                            <div className="text-xs uppercase tracking-[0.25em] text-primary font-mono mb-4">
                                {t("joinInstructor_pillars_pillar_label")} {p.num}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-3">
                                {p.tag}
                            </div>
                            <h3
                                className="text-xl sm:text-2xl md:text-3xl text-gray-900 dark:text-white font-bold mb-4"
                                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                            >
                                {p.title}
                            </h3>
                            <p className="text-gray-500 dark:text-white/55 text-sm leading-relaxed mb-6">{p.desc}</p>

                            {p.highlight ? (
                                <div className="mt-auto pt-5 border-t border-dashed border-gray-200 dark:border-white/10">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-2">
                                        {t("joinInstructor_pillar2_tickets")}
                                    </div>
                                    <div
                                        className="text-4xl font-bold text-primary"
                                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                                    >
                                        {p.highlight}
                                    </div>
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 dark:text-white/40 mt-3 leading-relaxed">
                                        {p.footer}
                                    </p>
                                </div>
                            ) : (
                                <div className="mt-auto pt-5 border-t border-dashed border-gray-200 dark:border-white/10 space-y-2 font-mono text-xs">
                                    {p.rows.map((r, idx) => (
                                        <div key={idx} className="flex justify-between text-gray-500 dark:text-white/55">
                                            <span>{r.label}</span>
                                            <span className="text-primary">{r.value}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </RevealOnScroll>
                ))}
            </div>
        </SectionShell>
    );
}
